import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { type ColumnDef } from '@tanstack/react-table'
import { CircleAlert, X } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import {
  SemanticApi,
  type SemanticArticle,
  type SemanticArticleSearch,
} from '@/services/targeting/targetingLookupApi'
import {
  semanticMatchLevel,
  semanticSearchLanguages,
  type Targeting,
} from '@/services/targeting/targeting'
import { formatCount, formatDateTime, formatNumber } from '@/utils/format'
import { SwitchField } from '../SwitchField'

const PAGE_SIZE = 100

/** How the sentiment analysis reads an article, the way the old UI buckets it. */
function SentimentBadge({ score }: { score: number }) {
  const { t } = useTranslation()
  const [key, variant] =
    score <= 0
      ? (['negative', 'warning'] as const)
      : score > 1
        ? (['positive', 'success'] as const)
        : (['neutral', 'blue'] as const)
  return (
    <Badge
      variant={variant}
      title={formatNumber(score, { maximumFractionDigits: 3, minimumFractionDigits: 3 })}
    >
      {t(`targeting.semantic.sentiment.${key}`)}
    </Badge>
  )
}

/** The article's headline, what it is about, where it lives and when it went up. */
function ArticleCell({ article }: { article: SemanticArticle }) {
  const { t } = useTranslation()
  const slash = article.url.indexOf('/')
  const host = slash > 0 ? article.url.slice(0, slash) : article.url
  const path = slash > 0 ? article.url.slice(slash) : ''

  return (
    <div className="flex min-w-0 gap-3">
      {article.image?.startsWith('http') && (
        <img
          src={article.image}
          alt=""
          loading="lazy"
          className="size-16 shrink-0 rounded-md bg-muted object-cover"
        />
      )}
      <div className="flex min-w-0 flex-col gap-0.5 whitespace-normal">
        <a
          href={`https://${article.url}`}
          target="_blank"
          rel="noreferrer"
          className="font-semibold text-primary hover:underline"
        >
          {article.title || t('targeting.semantic.noTitle')}
        </a>
        {article.description && (
          <span className="line-clamp-2 text-xs text-muted-foreground">{article.description}</span>
        )}
        <span className="text-xs break-all text-muted-foreground">
          <span className="font-medium text-foreground">{host}</span>
          {path}
        </span>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {article.publishedTime && <span>{formatDateTime(article.publishedTime)}</span>}
          {typeof article.sentimentScore === 'number' && (
            <SentimentBadge score={article.sentimentScore} />
          )}
        </div>
      </div>
    </div>
  )
}

export interface SemanticArticleResultsProps {
  /** The sentence and threshold to look for. */
  search: SemanticArticleSearch
  /** The targeting being edited — it narrows the search to the sites it reaches. */
  targeting: Targeting
  onClose: () => void
}

/**
 * The articles from the last 30 days that a semantic target would match, busiest
 * first — so a sentence and match level can be tried out before they are
 * added, and an existing target checked for what it actually reaches.
 */
export function SemanticArticleResults({
  search,
  targeting,
  onClose,
}: SemanticArticleResultsProps) {
  const { t } = useTranslation()
  const [totalHits, setTotalHits] = useState(false)
  const languages = semanticSearchLanguages(targeting)
  // The semantic targets themselves are left out of the search, so adding or
  // changing one should not throw away the results being looked at.
  const scope = useMemo(() => {
    const rest = { ...targeting }
    delete rest.semanticTargets
    return rest
  }, [targeting])

  const { data, isFetching, error } = useQuery({
    queryKey: ['semantic-articles', search, languages, totalHits, scope],
    queryFn: () =>
      SemanticApi.searchArticles(scope, {
        ...search,
        languages,
        totalHits,
        pageSize: PAGE_SIZE,
      }),
    staleTime: 5 * 60 * 1000,
  })

  const results = useMemo(() => data?.results ?? [], [data])
  const requests = useMemo(
    () => results.reduce((sum, row) => sum + (row.availableImpressionsLastDay ?? 0), 0),
    [results]
  )

  const columns = useMemo(
    (): ColumnDef<SemanticArticle, unknown>[] => [
      {
        id: 'article',
        header: t('targeting.semantic.article'),
        size: 420,
        meta: { minWidth: 280 },
        accessorFn: (row) => row.title ?? row.url,
        cell: ({ row }) => <ArticleCell article={row.original} />,
      },
      {
        id: 'match',
        header: t('targeting.semantic.match'),
        size: 180,
        accessorFn: (row) => row.score ?? 0,
        cell: ({ row }) => {
          const level = semanticMatchLevel(row.original.score)
          return level ? t(`targeting.semantic.levels.${level}`) : '—'
        },
      },
      {
        id: 'requests',
        header: t('targeting.semantic.adRequests'),
        size: 140,
        accessorFn: (row) => row.availableImpressionsLastDay ?? 0,
        cell: ({ getValue }) => (
          <span className="tabular-nums">{formatCount(getValue() as number)}</span>
        ),
      },
    ],
    [t]
  )

  return (
    <div className="flex flex-col gap-3 rounded-lg border p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-sm font-semibold">{t('targeting.semantic.resultsTitle')}</span>
          <span className="truncate text-xs text-muted-foreground">
            “{search.sentence}” ·{' '}
            {t(`targeting.semantic.levels.${semanticMatchLevel(search.score)}`)}
          </span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={t('targeting.semantic.closeResults')}
          onClick={onClose}
        >
          <X className="size-4" />
        </Button>
      </div>

      <SwitchField
        label={t('targeting.semantic.totalHits')}
        tooltip={t('targeting.semantic.totalHitsHelp')}
        checked={totalHits}
        onCheckedChange={setTotalHits}
      />

      {error ? (
        <p className="flex items-center gap-2 text-sm text-destructive">
          <CircleAlert className="size-4" />
          {error.message}
        </p>
      ) : (
        <>
          {!isFetching && results.length > 0 && (
            <div className="flex flex-col gap-1 text-xs text-muted-foreground">
              {totalHits && data && data.totalCount > results.length && (
                <p>
                  {t('targeting.semantic.totalSummary', {
                    pages: formatCount(data.totalCount),
                    requests: formatCount(data.totalAvailableImpressionsLastDay ?? 0),
                  })}
                </p>
              )}
              <p>
                {t(
                  results.length >= PAGE_SIZE
                    ? 'targeting.semantic.topSummary'
                    : 'targeting.semantic.allSummary',
                  { count: results.length, requests: formatCount(requests) }
                )}
              </p>
            </div>
          )}
          <DataTable
            columns={columns}
            data={results}
            loading={isFetching}
            getRowId={(row) => row.url}
            emptyMessage={t('targeting.semantic.noArticles')}
            maxHeight={480}
          />
        </>
      )}
    </div>
  )
}
